/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Input from '../form/Input';
import RadioButton from '../form/RadioButton';
import BottomBar from '../BottomBar';
import Dropdown from '../form/Dropdown';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { updateUsaDetails } from '../../../actions/preferences';

const ProfessionalDetail4 = ({
  insuranceCompanies,
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  errorCallback,
  draftValues,
  onDraftChange,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;

  const options = [
    { value: 'true', label: formatMessage({ defaultMessage: 'Yes' }) },
    {
      value: 'false',
      label: formatMessage({ defaultMessage: 'No, Out of network' }),
    },
  ];

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: draftValues || {
      in_network: 'false',
      plans: [],
      npi_number_individual: '',
      npi_number_business: '',
      tax_id: '',
      medicare_number: '',
      medicaid_number: '',
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const inNetwork = watch('in_network');

  const onSubmit = async values => {
    const errorMessage = (
      <FormattedMessage defaultMessage="There was a problem updating your professional details." />
    );

    const usaProviderValues = {
      ...values,
      network_plans: values.plans,
    };

    openLoadingPanel(
      <FormattedMessage defaultMessage="Updating professional details..." />,
    );

    try {
      await updateUsaDetails(usaProviderValues);
      closeLoadingPanel();
      nextPage();
    } catch (error) {
      errorCallback(error, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>
          <FormattedMessage defaultMessage="Professional Details" />
        </h1>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx, react/jsx-no-literals */}
        <div className="sub-page-indicator">4/4</div>
      </div>

      <p className="onboarding-label">
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {`${formatMessage({
          defaultMessage: 'Are you in network with any health plans?',
        })} *`}
      </p>
      <Controller
        name="in_network"
        control={control}
        rules={{ required: formatMessage({ defaultMessage: 'Required' }) }}
        render={({ field }) => (
          <RadioButton
            {...field}
            options={options}
            error={errors.in_network?.message}
          />
        )}
      />

      {inNetwork === 'true' && (
        <div>
          <p className="onboarding-label">
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {`${formatMessage({
              defaultMessage: 'Which health plans are you associated with?',
            })} *`}
          </p>
          <div className="input-row">
            <Controller
              name="plans"
              control={control}
              rules={{
                validate: value =>
                  value && value.length > 0
                    ? true
                    : formatMessage({
                        defaultMessage: 'You must select at least 1 plan',
                      }),
              }}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  searchable
                  multi
                  width={300}
                  options={insuranceCompanies}
                  error={errors.plans?.message}
                />
              )}
            />
          </div>

          <div className="input-row">
            <Controller
              name="npi_number_individual"
              control={control}
              rules={{
                required: formatMessage({ defaultMessage: 'Required' }),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={
                    <FormattedMessage defaultMessage="NPI Individual Number" />
                  }
                  error={errors.npi_number_individual?.message}
                />
              )}
            />
            <Controller
              name="npi_number_business"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label={
                    <FormattedMessage defaultMessage="NPI Business Number" />
                  }
                  error={errors.npi_number_business?.message}
                />
              )}
            />
          </div>
        </div>
      )}

      <div className="input-row">
        <Controller
          name="tax_id"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={<FormattedMessage defaultMessage="Tax ID" />}
              error={errors.tax_id?.message}
            />
          )}
        />
        <Controller
          name="medicare_number"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={
                <FormattedMessage defaultMessage="Individual Medicare Number" />
              }
              error={errors.medicare_number?.message}
            />
          )}
        />
        <Controller
          name="medicaid_number"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={
                <FormattedMessage defaultMessage="Individual Medicaid Number" />
              }
              error={errors.medicaid_number?.message}
            />
          )}
        />
      </div>

      <BottomBar />
    </form>
  );
};

ProfessionalDetail4.propTypes = {
  insuranceCompanies: PropTypes.array.isRequired,
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default ProfessionalDetail4;
